import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { currentUser } from '../../../../../core/auth';
import { CompanyService } from '../../../../../core/auth/_services/company.service';
import { ProductService, WorkOrderService } from '../../../../../core/plant-configuration';
import { AppState } from '../../../../../core/reducers';
import { QueryParamsModel, QueryResultsModel } from '../../../../../core/_base/crud';

@Component({
  selector: 'kt-work-order-list',
  templateUrl: './work-order-list.component.html',
  styleUrls: ['./work-order-list.component.scss']
})
export class WorkOrderListComponent implements OnInit, OnDestroy {
  readonly sourceOptions = ['MANUAL', 'ERP'];
  inited = false;
  products: any[] = [];
  statusOptions: any[] = [];
  dataObs = new Subject<QueryResultsModel>();
  deleteObs = new Subject<string>();
  private subscriptions: Subscription[] = [];

  filterList: any[] = [
    {name: 'status_id', value: '-1', type: 'list', label: 'Estado', optionList: []},
    {name: 'product_id', value: '-1', type: 'list', label: 'Producto', optionList: []},
    {name: 'source_type', value: '-1', type: 'list', label: 'Fuente', optionList: this.sourceOptions.map(value => ({text: value, value}))},
    {name: 'planned_start_from', value: null, type: 'date', label: 'Inicio Planificado'},
    {name: 'planned_end_to', value: null, type: 'date', label: 'Fin Planificado'},
    {name: 'search', value: '', type: 'text', label: 'N° de orden', placeholder: 'Buscar por N° de orden'}
  ];

  columnList: any[] = [
    {name: 'work_order_number', displayName: 'N° Orden', def: 'work_order_number'},
    {name: 'products', displayName: 'Productos planificados', def: 'products', transformFcn: row => this.productSummary(row)},
    {name: 'status_id', displayName: 'Estado', def: 'status'},
    {name: 'planned_start', displayName: 'Inicio planificado', def: 'planned_start', isDate: true},
    {name: 'planned_end', displayName: 'Fin planificado', def: 'planned_end', isDate: true},
    {name: 'source_type', displayName: 'Fuente', def: 'source_type', transformFcn: row => this.getSourceName(row)}
  ];
  actions = {add: true, edit: true, delete: false};

  constructor(
    private workOrderService: WorkOrderService,
    private productService: ProductService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private companyService: CompanyService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(combineLatest([
      this.store.pipe(select(currentUser), filter(user => !!user && Number(user.id) > 0)),
      this.companyService.companyLoaded$
    ]).pipe(
      map(([user, loaded]) => {
        if (Number(user.group_id) !== 4) return Number(user.company_id);
        const company = loaded ? this.companyService.getCurrentCompany() : null;
        return company && Number(company.id);
      }),
      filter(companyId => Number.isInteger(companyId) && companyId > 0),
      distinctUntilChanged(),
      switchMap(companyId => this.productService.getAll({company_id: companyId}))
    ).subscribe(products => {
      this.products = products || [];
      this.getFilter('product_id').optionList = this.products.map(product => ({text: product.name, value: product.id}));
      this.inited = true;
      this.cdr.detectChanges();
    }, error => {
      console.error('Error al cargar productos para órdenes de trabajo', error);
      this.inited = true;
      this.cdr.detectChanges();
    }));
  }

  ngOnDestroy(): void { this.subscriptions.forEach(subscription => subscription.unsubscribe()); }

  getPage(query: QueryParamsModel): void {
    if (query.filter.planned_start_from) query.filter.planned_start_from = this.formatDate(query.filter.planned_start_from);
    if (query.filter.planned_end_to) query.filter.planned_end_to = this.formatDate(query.filter.planned_end_to);
    this.subscriptions.push(this.workOrderService.getPage(query).subscribe((response: any) => {
      const data = response && response.data ? response.data : response;
      const rows = data.items || data.rows || [];
      const totalCount = data.totalCount !== undefined ? data.totalCount : (data.total !== undefined ? data.total : rows.length);
      this.collectStatuses(rows);
      this.dataObs.next({items: rows, totalCount} as QueryResultsModel);
      this.cdr.detectChanges();
    }, error => {
      console.error('Error al cargar órdenes de trabajo', error);
      this.dataObs.next({items: [], totalCount: 0} as QueryResultsModel);
      this.cdr.detectChanges();
    }));
  }

  getLots(row: any): any[] { return row && (row.Lots || row.lots || row.WorkOrderLots || row.work_order_lots) || []; }

  getProductName(item: any): string {
    const product = item && (item.Product || item.product);
    if (product && product.name) return product.name;
    const match = this.products.find(option => String(option.id) === String(item && item.product_id));
    return match ? match.name : 'Sin producto';
  }

  productSummary(workOrder: any): string {
    let products = workOrder && (workOrder.Products || workOrder.products) || [];
    // Compatibilidad de lectura con órdenes creadas antes del detalle multiproducto.
    if (!products.length && workOrder && workOrder.product_id) {
      products = [{product_id: workOrder.product_id, planned_units: workOrder.planned_units, Product: workOrder.Product}];
    }
    if (!products.length) return 'Sin productos';
    return products.map(item => {
      const product = item.Product || item.product;
      const name = product && product.name || this.getProductName(item);
      const units = item.planned_units !== null && item.planned_units !== undefined ? item.planned_units : '-';
      return name + ': ' + units;
    }).join(', ');
  }

  getStatusName(item: any): string {
    const status = item && (item.Status || item.WorkOrderStatus || item.LotStatus || item.status);
    if (status && status.name) return status.name;
    const match = this.statusOptions.find(option => String(option.id) === String(item && item.status_id));
    return match ? match.name : (item && item.status_name) || 'Sin estado';
  }

  getStatusClass(item: any): string {
    const value = this.getStatusName(item).toLowerCase();
    if (value.indexOf('activa') >= 0 || value.indexOf('producción') >= 0 || value.indexOf('produccion') >= 0) return 'status-badge status-badge--success';
    if (value.indexOf('pendiente') >= 0 || value.indexOf('planific') >= 0) return 'status-badge status-badge--warning';
    return 'status-badge status-badge--neutral';
  }

  getOperatorName(item: any): string {
    const operator = item && (item.Operator || item.operator || item.CreatedByOperator || item.created_by_operator);
    return operator && (operator.fullname || operator.name || operator.login) || (item && item.operator_name) || 'Sin operador';
  }

  getSourceName(item: any): string {
    const source = item && (item.Source || item.source || item.WorkOrderSource);
    return (item && item.source_type) || (source && (source.code || source.name)) || 'MANUAL';
  }

  getReference(item: any): string { return item && (item.source_reference || item.reference) || '-'; }

  editLots(row: any, lot?: any): void {
    const queryParams: any = {step: 'lots'};
    if (lot && lot.id) queryParams.lot_id = lot.id;
    this.router.navigate(['/plant-configuration/work-orders/edit', row.id], {queryParams});
  }

  private getFilter(name: string): any { return this.filterList.find(filter => filter.name === name); }

  private collectStatuses(rows: any[]): void {
    const known: {[key: string]: any} = {};
    this.statusOptions.forEach(status => known[String(status.id)] = status);
    rows.forEach(row => {
      if (row.status_id === undefined || row.status_id === null) return;
      const status = row.Status || row.WorkOrderStatus || row.status;
      known[String(row.status_id)] = {id: row.status_id, name: status && status.name || row.status_name || 'Estado ' + row.status_id};
    });
    this.statusOptions = Object.keys(known).map(key => known[key]);
    this.getFilter('status_id').optionList = this.statusOptions.map(status => ({text: status.name, value: status.id}));
  }

  private formatDate(value: any): string {
    const date = new Date(value);
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  }
}
